import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface PaintRowContextMenuProps {
  x: number;
  y: number;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onClose: () => void;
}

export function PaintRowContextMenu({
  x,
  y,
  onDuplicate,
  onDelete,
  onClose,
}: PaintRowContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(() => ({ left: x, top: y }));

  useLayoutEffect(() => {
    function updatePosition(): void {
      const menu = menuRef.current;
      const margin = 8;
      const width = menu?.offsetWidth ?? 160;
      const height = menu?.offsetHeight ?? 80;
      setPosition({
        left: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - width - margin)),
        top: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - height - margin)),
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [x, y]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close context menu"
        className="fixed inset-0 z-40 cursor-default"
        onClick={onClose}
        onContextMenu={event => {
          event.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="fixed z-50 overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 text-[12px] text-slate-200 shadow-xl shadow-black/30"
        style={{ left: position.left, top: position.top, width: "min(10rem, calc(100vw - 16px))" }}
        role="menu"
      >
        {onDuplicate ? (
          <button
            type="button"
            className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
            onClick={() => {
              onDuplicate();
              onClose();
            }}
            role="menuitem"
          >
            Duplicate
          </button>
        ) : null}
        {onDelete ? (
          <button
            type="button"
            className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08]"
            onClick={() => {
              onDelete();
              onClose();
            }}
            role="menuitem"
          >
            Delete
          </button>
        ) : null}
      </div>
    </>,
    document.body,
  );
}
